import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { GapSmall } from 'components/GapSmall';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { formatDate } from 'lib/riskbase';
import { smartText } from 'lib/str';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  doAseSetFlagResult,
} from 'store/actions';
import {
  selectAseFlagHistoryForFlag,
  selectAseFlagResult,
  selectAseStatus,
  selectAseTplFlag,
} from 'store/assessments/selectors';
import { AssessmentStatusesEnum } from 'types/types';

export const FlagModal: React.FC<ModalScreenProps<ModalScreens.AseFlag>> = ({
  route,
  navigation,
}) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();

  const { flagId } = route.params;

  const aseStatus = useSelector(selectAseStatus).id as AssessmentStatusesEnum;
  const flag = useSelector((state) => selectAseTplFlag(state, flagId));
  const flagResult = useSelector((state) => selectAseFlagResult(state, flagId));
  const flagHistory = useSelector((state) =>
    selectAseFlagHistoryForFlag(state, flagId),
  );
  const flagHistoryReversed = React.useMemo(
    () =>
      cloneDeep(flagHistory)
        .reverse()
        .filter((history) => !!history.deleted),
    [flagHistory],
  );

  const forceUpdate = useForceUpdate();

  const initValues = React.useRef({
    comment: '',
  });
  const changes = React.useRef(cloneDeep(initValues.current));
  const checkIsDirty = React.useCallback(
    () => !isEqual(initValues.current, changes.current),
    [],
  );
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const handleChangeWithName = React.useCallback(
    <
      N extends keyof typeof changes.current,
      V extends (typeof changes.current)[N],
    >(
      name: N,
      value: V,
    ) => {
      changes.current[name] = value;
      if (isDirty.current !== checkIsDirty()) {
        forceUpdate();
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const handleSave = React.useCallback(() => {
    dispatch(
      doAseSetFlagResult({
        flagId,
        comment: changes.current.comment,
        doDelete: false,
      }),
    );
  }, [dispatch, flagId]);

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, handleSave, dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  if (!flag) {
    return null;
  }

  const canEdit = flag.allowedStatuses.includes(aseStatus);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Flag"
          saveButtonText={flagResult ? 'Unset Flag' : 'Set Flag'}
          onPressCancelButton={handleClose}
          onPressSaveButton={
            canEdit
              ? flagResult
                ? () =>
                    dispatch(
                      NAVIGATE_MODAL({
                        screen: ModalScreens.AseFlagUnset,
                        params: {
                          flagId,
                        },
                      }),
                    )
                : handleSave
              : undefined
          }
        />
      }
    >
      <Card>
        <CardRow label={flag.name} value={flag.description} labelAlignTop />
      </Card>
      {flagResult ? (
        <Card>
          <CardRow
            value={formatDate(flagResult.timestamp, UI_DATE_FORMAT_LONG)}
          />
          <CardRow value={`${flagResult.by.name} (${flagResult.by.email})`} />
          <CardRow
            label="Comment"
            value={flagResult.comment}
            placeholder="No Comment"
            labelAlignTop
          />
        </Card>
      ) : canEdit ? (
        <Card
          label="Optional Comment"
          explanation={smartText(
            "This flag is not set. Press 'Set Flag' to set it",
          )}
        >
          <TextAreaField
            name="comment"
            placeholder="No Comment"
            onChangeWithName={handleChangeWithName}
            onEsc={handleClose}
            autoFocusWeb
            autoFocusDelay
          />
        </Card>
      ) : (
        <Card explanation={smartText('This flag is not set')} />
      )}
      <SeparatorHorizontal />
      {!!flagHistoryReversed.length && (
        <View>
          <Card
            label="History"
            explanation={
              !flagHistoryReversed.length
                ? 'No history for this flag'
                : undefined
            }
          />
          <GapSmall>
            {flagHistoryReversed.map((history, index) => (
              <Card key={index}>
                <CardRow
                  label={`Created on ${formatDate(
                    history.created.timestamp,
                    UI_DATE_FORMAT_LONG,
                  )}`}
                  label2={`${history.created.by.name} (${history.created.by.email})`}
                  value={history.created.comment}
                  labelAlignTop
                />
                {history.deleted && (
                  <CardRow
                    label={`Deleted on ${formatDate(
                      history.deleted.timestamp,
                      UI_DATE_FORMAT_LONG,
                    )}`}
                    label2={`${history.deleted.by.name} (${history.deleted.by.email})`}
                    value={history.deleted.comment}
                    labelAlignTop
                  />
                )}
              </Card>
            ))}
          </GapSmall>
        </View>
      )}
    </Page>
  );
};
